import { BarcodeScannerUI } from "@/utils/barcode-scanner-ui";
import { useI18n } from "@/composables/useI18n";
import {
  BarcodeScanner,
  BarcodeFormat,
  type Barcode,
} from "@capacitor-mlkit/barcode-scanning";
import { onBeforeUnmount, onDeactivated } from "vue";

export function useBarcodeScanner(
  onBarcodeScanned: (code: Barcode) => void | Promise<void>
) {
  const { t } = useI18n();
  let scanning = false;
  const barcodeUIBuilder = new BarcodeScannerUI();

  const stopScan = async () => {
    if (!scanning) return;
    scanning = false;
    document.body.style.display = "block";
    await BarcodeScanner.removeAllListeners();
    await BarcodeScanner.stopScan();
    barcodeUIBuilder.remove();
  };

  onDeactivated(() => {
    stopScan();
  });

  onBeforeUnmount(() => {
    stopScan();
  });

  const openBarcodeScanner = async () => {
    const status = await BarcodeScanner.checkPermissions();
    if (!status.camera) {
      const { camera } = await BarcodeScanner.requestPermissions();
      if (!camera) return false;
    }
    scanning = true;
    try {
      document.body.style.display = "none";
      barcodeUIBuilder.make(stopScan, t("common.qrcode"));
      const listener = await BarcodeScanner.addListener(
        "barcodeScanned",
        async (result) => {
          if (result.barcode) {
            listener.remove();
            stopScan();
            return onBarcodeScanned(result.barcode);
          }
        }
      );
      await BarcodeScanner.startScan({
        formats: [BarcodeFormat.QrCode],
      });
    } catch (e) {
      console.error(e);
    }
  };

  return { openBarcodeScanner };
}
